// Chooses the recording behind an audio prompt: an existing asset from the
// tenant's audio store, or a new upload. Prompts imported from Teams show the
// Teams file name and, once a matching recording exists (uploaded here or via
// the export's audio folder), link to it automatically.

import { useMemo, useRef, useState } from 'react';
import { Upload, Play, Square, Link2, X } from 'lucide-react';
import { api, type AudioAssetMeta, type Prompt } from '../../api/client';
import { inputCls } from './common';

export interface AudioPickerProps {
  value: Prompt;
  onChange: (v: Prompt) => void;
  assets: AudioAssetMeta[];
  onUpload: (file: File, meta: { teamsAudioFileId?: string; fileName?: string }) => Promise<AudioAssetMeta>;
}

function bytesText(n: number): string {
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;
}

export function AudioPicker({ value, onChange, assets, onUpload }: AudioPickerProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const linked = useMemo(() => {
    if (value.audioId) return assets.find(a => a.id === value.audioId);
    if (value.teamsAudioFileId) return assets.find(a => a.teamsAudioFileId === value.teamsAudioFileId);
    return undefined;
  }, [value.audioId, value.teamsAudioFileId, assets]);

  async function upload(file: File) {
    setBusy(true); setError(null);
    try {
      const asset = await onUpload(file, { teamsAudioFileId: value.teamsAudioFileId, fileName: value.teamsFileName || file.name });
      onChange({ ...value, audioId: asset.id });
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally { setBusy(false); }
  }

  async function togglePreview() {
    const target = linked?.id;
    if (!target) return;
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    try {
      const { url } = await api.getCallFlowAudioUrl(target);
      setPreviewUrl(url);
      setPlaying(true);
      setTimeout(() => { audioRef.current?.play().catch(() => setPlaying(false)); }, 0);
    } catch (e: any) { setError(e?.message || 'Could not load the recording'); }
  }

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {linked ? (
          <>
            <Link2 className="w-3.5 h-3.5 text-green-600" />
            <span className="font-medium text-gray-800 truncate max-w-[14rem]" title={linked.fileName}>{linked.fileName}</span>
            <span className="text-gray-400">{bytesText(linked.bytes)}{linked.source === 'teams' ? ' · from Teams' : ''}</span>
            <button type="button" onClick={togglePreview} className="inline-flex items-center gap-1 px-2 py-0.5 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50">
              {playing ? <Square className="w-3 h-3" /> : <Play className="w-3 h-3" />}{playing ? 'Stop' : 'Play'}
            </button>
            {value.audioId && (
              <button type="button" onClick={() => onChange({ ...value, audioId: undefined })} className="inline-flex items-center gap-1 text-gray-400 hover:text-red-600" title="Unlink this recording">
                <X className="w-3 h-3" />unlink
              </button>
            )}
            <audio ref={audioRef} src={previewUrl || undefined} onEnded={() => setPlaying(false)} onPause={() => setPlaying(false)} className="hidden" />
          </>
        ) : (
          <span className="text-amber-700">
            {value.teamsFileName || value.teamsAudioFileId
              ? <>Teams recording <span className="font-medium">{value.teamsFileName || value.teamsAudioFileId}</span> — not uploaded yet</>
              : 'No recording linked yet'}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={value.audioId || ''} onChange={e => onChange({ ...value, audioId: e.target.value || undefined })} className={`${inputCls} w-auto max-w-full`}>
          <option value="">{linked && !value.audioId ? `— auto-linked: ${linked.fileName} —` : '— choose an existing recording —'}</option>
          {assets.map(a => <option key={a.id} value={a.id}>{a.fileName}{a.source === 'teams' ? ' (Teams)' : ''}</option>)}
        </select>
        <input ref={fileRef} type="file" accept="audio/*,.wav,.mp3,.m4a,.ogg" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" />{busy ? 'Uploading…' : 'Upload recording'}
        </button>
      </div>
      {error && <div className="text-[11px] text-red-600">{error}</div>}
    </div>
  );
}
